/**
 * ********************************************************************************************************************
 * Responsibility - Renders a list of proofs. Provides actions to accept all or reject all proofs. 🐺
 * **************************************************************************************************
 *
 * * @format
 *
 * @format
 */

import {
    DESIGN_STATUS_NOT_REVIEWED,
    DESIGN_STATUS_DISAPPROVED,
    PROOF_STATUS_DISAPPROVED,
    PROOF_STATUS_APPROVED,
    PROOF_STATUS_PENDING,
    PROOF_TYPE_PRINTED,
    COLOR_5C
} from 'frontier-constants';
import {ProofPressReady} from './single_design_proof/proof_press_ready/proof_press_ready';
import {useProofingContext} from './proofing_context_provider';
import {SpotwhiteNotice} from './utils/spotwhite_notice';
import {RejectAll} from './modals/disapprove_all_modal';
import {ApproveAll} from './modals/approve_all_modal';
import React, {useState, useEffect} from 'react';
import {If, IfElse} from 'frontier-utilities';
import {Toast} from '@sttark/design-system';

// styles
import './proofs_list.sass';

export const ProofsList = () => {
    const proofingCtx = useProofingContext();

    const {reviewNotesVerification, specsVerification, designsOrders, currentProof, order} =
        proofingCtx.data;

    const canContinueWithApproval =
        reviewNotesVerification.isVerified && specsVerification.isVerified;
    const isProofComplete =
        currentProof.status === PROOF_STATUS_APPROVED ||
        currentProof.status === PROOF_STATUS_DISAPPROVED;
    const pendingDesigns = designsOrders.filter(
        designOrder => designOrder.status === DESIGN_STATUS_NOT_REVIEWED
    )?.length;
    const isAllPendingDesigns = pendingDesigns === designsOrders.length;
    const updateProofStatusBtnLabel = isAllPendingDesigns
        ? 'All'
        : pendingDesigns + ' Pending Designs';

    const [toast, setToast] = useState({
        isDanger: false,
        isOpen: false,
        message: ''
    });

    const handleRejected = () => {
        setToast({
            message: 'Thank you for reviewing all designs! We are continuing to process your order',
            isDanger: false,
            isOpen: true
        });
    };

    const handleApproved = () => {
        setToast({
            message: 'Proof approved! We are continuing to process your order',
            isDanger: false,
            isOpen: true
        });
    };

    const handleCloseToast = () => {
        setToast({
            isDanger: false,
            isOpen: false,
            message: ''
        });
    };

    useEffect(() => {
        const showSubmissionToast = sessionStorage.getItem('showSubmissionToast');
        if (showSubmissionToast === 'true') {
            const orderHasAtLeastOneRejectedDesign = designsOrders.some(
                designOrder => designOrder.proof_status_id === DESIGN_STATUS_DISAPPROVED
            );

            if (orderHasAtLeastOneRejectedDesign) handleRejected();
            else handleApproved();

            sessionStorage.removeItem('showSubmissionToast');
        }
    }, [designsOrders]);

    return (
        <>
            <div className="browser-proofing-77tt__title">
                <div className="d-flex align-items-start justify-content-start gap-3">
                    <If condition={isProofComplete}>
                        <i className="fa fa-check color-accent-primary" style={{fontSize: 24}} />
                    </If>
                    <h3 className="color-structure-black m-0">Step 3. Review Artwork</h3>
                </div>
                <IfElse condition={!canContinueWithApproval}>
                    <div className="browser-proofing-77tt-title_alert d-flex align-items-center justify-content-start gap-2">
                        <i className="fa fa-warning color-alert-negative" />
                        <h6 className="m-0 color-alert-negative">
                            Approve specs and notes to review artwork
                        </h6>
                    </div>
                    <If condition={order.color_id === COLOR_5C}>
                        <div className="browser-proofing-77tt-title_spotwhite d-flex align-items-center justify-content-start gap-2">
                            <i className="fa fa-circle-info" />
                            <p className="m-0 p-0 color-structure-darker">
                                <SpotwhiteNotice substrateId={order.substrate_id} />
                            </p>
                        </div>
                    </If>
                </IfElse>
            </div>

            <div className={`browser-proofing-77tt`}>
                <Toast
                    onClose={handleCloseToast}
                    danger={toast.isDanger}
                    autoHideDuration={4000}
                    isOpen={toast.isOpen}
                    zIndex={3001}
                    autoHide>
                    <p className="color-structure-white m-0">{toast.message}</p>
                </Toast>

                {/* set up an overlay so the user can't click anything */}
                <If condition={!canContinueWithApproval}>
                    <div className={'table-disabled'} />
                </If>

                {designsOrders.map(designOrder => {
                    return <ProofPressReady key={designOrder.id} designOrder={designOrder} />;
                })}

                <If
                    condition={
                        currentProof.status === PROOF_STATUS_PENDING && designsOrders.length >= 5
                    }>
                    <div className="browser-proofing-77tt__approve-reject-all d-flex align-items-center justify-content-start gap-4">
                        <ApproveAll>
                            <button className="btn btn--secondary btn-sm d-flex color-accent-primary gap-2 button--success w-100">
                                <i className="fa fa-check" />
                                <b>Approve {updateProofStatusBtnLabel}</b>
                            </button>
                        </ApproveAll>
                        <RejectAll>
                            <button className="btn btn--secondary btn-sm d-flex color-alert-negative gap-2 button--danger w-100 ">
                                <i className="fa fa-close" />
                                <b>Reject {updateProofStatusBtnLabel}</b>
                            </button>
                        </RejectAll>
                    </div>
                </If>
            </div>
        </>
    );
};
